import { useState, type ReactNode } from "react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";

/** These captures are real app screens with anonymized, illustrative demo data. */
const workspaceCapture = (id: string, label: string, height = 1000) => ({
  src: `/images/home-v3/${id}.webp`,
  alt: `${label} workspace in PadPulse with anonymized, illustrative demo data`,
  label,
  width: 1440,
  height,
});

export const TOOLKIT_SCREENSHOTS: Readonly<Record<string, { src: string; alt: string; label: string; width: number; height: number }>> = {
  "property-management": workspaceCapture("property-management", "Property management", 955),
  underwriting: workspaceCapture("underwriting", "Underwriting analysis", 955),
  "direct-booking": workspaceCapture("direct-booking", "Direct booking website"),
  "listing-management": workspaceCapture("listing-management", "Listing management"),
  "dynamic-pricing": workspaceCapture("dynamic-pricing", "Dynamic pricing"),
  "smart-devices": workspaceCapture("smart-devices", "Smart locks"),
  "ai-tools": workspaceCapture("ai-tools", "Room image tools", 955),
  padsplit: workspaceCapture("padsplit", "PadSplit connection"),
  team: workspaceCapture("team", "Team and collaboration"),
  dashboard: workspaceCapture("dashboard", "Portfolio dashboard"),
  members: {
    src: "/images/home-v2/messages.webp",
    alt: "PadPulse team messaging inbox and a resident conversation using illustrative demo data",
    label: "Member messaging",
    width: 1440,
    height: 1000,
  },
  operations: {
    src: "/images/home-v2/maintenance.webp",
    alt: "PadPulse maintenance task list using illustrative demo data",
    label: "Maintenance tasks",
    width: 1440,
    height: 1000,
  },
  financials: {
    src: "/images/home-v2/numbers-report.webp",
    alt: "Cropped PadPulse property profit and loss report using illustrative demo data",
    label: "Property financials",
    width: 1192,
    height: 825,
  },
};

export function FeatureExplorerMedia({ groupId, illustration, onExpandedChange }: { groupId: string; illustration: ReactNode; onExpandedChange?: (expanded: boolean) => void }) {
  const [expanded, setExpanded] = useState(false);
  const changeExpanded = (value: boolean) => {
    setExpanded(value);
    onExpandedChange?.(value);
  };
  const capture = TOOLKIT_SCREENSHOTS[groupId];
  if (!capture) {
    return (
      <div data-testid={`illustrated-workflow-${groupId}`}>
        <p className="border-b border-gray-800 px-4 py-3 text-xs leading-5 text-slate-300">
          Illustrated workflow — no reviewed app screenshot is available for this workspace yet.
        </p>
        {illustration}
      </div>
    );
  }
  return (
    <figure className="p-4" data-testid={`toolkit-screenshot-${groupId}`}>
      <button
        type="button"
        className="block w-full overflow-hidden rounded-lg border border-white/20 bg-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-brand-accent"
        aria-label={`Enlarge ${capture.label} screenshot`}
        onClick={() => changeExpanded(true)}
      >
        <img src={capture.src} alt={capture.alt} width={capture.width} height={capture.height} loading="lazy" decoding="async" className="h-auto w-full" />
      </button>
      <figcaption className="mt-3 text-xs leading-5 text-slate-300">
        Actual PadPulse {groupId === "financials" ? "P&L report (cropped)" : "app"} · illustrative demo data, not live operating results. Select the image to enlarge.
      </figcaption>
      <Dialog open={expanded} onOpenChange={changeExpanded}>
        <DialogContent className="max-h-[90dvh] max-w-6xl overflow-y-auto">
          <DialogTitle>{capture.label}</DialogTitle>
          <DialogDescription>Actual PadPulse interface with anonymized, illustrative demo data.</DialogDescription>
          <img src={capture.src} alt={capture.alt} width={capture.width} height={capture.height} decoding="async" className="h-auto w-full rounded-md" />
        </DialogContent>
      </Dialog>
    </figure>
  );
}
